import Fuse from 'fuse.js';

const normaliseText=(value)=> String(value || '').trim().toLowerCase();

const getDomain=(url)=> {
  try {
    return new URL(url).hostname.replace(/^www\./,'');
  } catch {
    return '';
  }
};

const buildSearchDocuments=(bookmarks=[],folders=[])=> {
  const folderNames=new Map((folders || []).map(folder=> [folder.id,folder.name || '']));
  return (bookmarks || []).map(bookmark=> ({
    ...bookmark,
    _searchDomain: getDomain(bookmark.url),
    _searchFolder: bookmark.folderId ? (folderNames.get(bookmark.folderId) || '') : 'Unfiled'
  }));
};

const bookmarkSearchOptions={
  keys: [
    {name: 'title',weight: 0.45},
    {name: '_searchDomain',weight: 0.2},
    {name: 'tags',weight: 0.15},
    {name: '_searchFolder',weight: 0.1},
    {name: 'url',weight: 0.06},
    {name: 'description',weight: 0.04}
  ],
  threshold: 0.4,
  distance: 100,
  includeScore: true,
  ignoreLocation: true,
  minMatchCharLength: 2
};

const folderSearchOptions={
  keys: [{name: 'name',weight: 1}],
  threshold: 0.35,
  distance: 60,
  includeScore: true,
  ignoreLocation: true,
  minMatchCharLength: 2
};

const calculateRank=(result,query)=> {
  const item=result.item;
  const q=normaliseText(query);
  const title=normaliseText(item.title);
  const domain=normaliseText(item._searchDomain);
  const folder=normaliseText(item._searchFolder);
  const tags=Array.isArray(item.tags)
    ? item.tags.map(normaliseText)
    : normaliseText(item.tags).split(',').map(tag=> tag.trim()).filter(Boolean);

  let score=(1-(result.score ??1))*100;

  if (title===q) score+=70;
  else if (title.startsWith(q)) score+=45;
  else if (title.includes(q)) score+=25;

  if (domain===q) score+=45;
  else if (domain.startsWith(q)) score+=30;
  else if (domain.includes(q)) score+=20;

  if (tags.some(tag=> tag===q)) score+=35;
  else if (tags.some(tag=> tag.includes(q))) score+=15;

  if (folder===q) score+=25;
  else if (folder.includes(q)) score+=10;

  return score;
};

export const searchBookmarks=(bookmarks,query,folders=[])=> {
  const trimmedQuery=String(query || '').trim();
  if (!trimmedQuery) return [];

  const documents=buildSearchDocuments(bookmarks,folders);
  const fuse=new Fuse(documents,bookmarkSearchOptions);

  return fuse.search(trimmedQuery)
    .map(result=> {
      const {_searchDomain,_searchFolder,...bookmark}=result.item;
      return {
        ...bookmark,
        _searchScore: calculateRank(result,trimmedQuery)
      };
    })
    .sort((a,b)=> b._searchScore-a._searchScore);
};

export const searchFolders=(folders,query)=> {
  const trimmedQuery=String(query || '').trim();
  if (!trimmedQuery) return [];

  const fuse=new Fuse(folders || [],folderSearchOptions);
  const q=normaliseText(trimmedQuery);

  return fuse.search(trimmedQuery)
    .map(result=> {
      const name=normaliseText(result.item.name);
      let score=(1-(result.score ??1))*100;
      if (name===q) score+=50;
      else if (name.startsWith(q)) score+=30;
      else if (name.includes(q)) score+=15;
      return {...result.item,_searchScore: score};
    })
    .sort((a,b)=> b._searchScore-a._searchScore);
};

export const getSearchSuggestions=(bookmarks,query)=> {
  if (!query || query.trim()==='') return [];

  const suggestions=new Set();
  const lowerQuery=query.toLowerCase();

  (bookmarks || []).forEach(bookmark=> {
    if (Array.isArray(bookmark.tags)) {
      bookmark.tags.forEach(tag=> {
        if (String(tag).toLowerCase().includes(lowerQuery)) suggestions.add(tag);
      });
    }

    if (bookmark.title) {
      bookmark.title.split(/\s+/).forEach(word=> {
        const cleanWord=word.replace(/[^\w-]/g,'');
        if (cleanWord.toLowerCase().includes(lowerQuery) && cleanWord.length >2) {
          suggestions.add(cleanWord);
        }
      });
    }

    const domain=getDomain(bookmark.url);
    if (domain && domain.toLowerCase().includes(lowerQuery)) suggestions.add(domain);
  });

  return Array.from(suggestions).slice(0,6);
};